import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function AuthLayout({ setView, setUser }) {
  const { tenantConfig } = useTheme();
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  // Parse URL query params and paths
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search || '') : null;
  const initialInviteToken = urlParams?.get('invite') || urlParams?.get('ref') || '';
  const initialApplyAgent = typeof window !== 'undefined' && (
    window.location.pathname.includes('/apply-agent') || urlParams?.get('apply') === 'agent'
  );

  // Authentication mode states
  const [isLogin, setIsLogin] = useState(!initialInviteToken && !initialApplyAgent);
  const [isAgentApply, setIsAgentApply] = useState(initialApplyAgent);
  
  // Standard credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('END_USER');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Two-Factor Authentication states
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [twoFactorPin, setTwoFactorPin] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Operator / Team Invite Flow states
  const [inviteToken, setInviteToken] = useState(initialInviteToken);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(Boolean(initialInviteToken));
  const [inviteError, setInviteError] = useState('');
  const [phone, setPhone] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgWebsite, setOrgWebsite] = useState('');
  const [orgEnquiriesEmail, setOrgEnquiriesEmail] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('');

  // Agent Application Flow states
  const [agentForm, setAgentForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    company_name: '',
    billing_country: '',
    experience: '',
    payout_method: 'paypal',
    paypal_email: '',
    bank_name: '',
    account_number: '',
    routing_swift: ''
  });
  const [agentApplySuccess, setAgentApplySuccess] = useState(false);

  // Beta Access Request states
  const initialApplyBeta = typeof window !== 'undefined' && (
    window.location.pathname.includes('/beta-request') || urlParams?.get('apply') === 'beta' || urlParams?.get('request') === 'beta'
  );
  const [showBetaModal, setShowBetaModal] = useState(initialApplyBeta);
  const [betaEmail, setBetaEmail] = useState('');
  const [betaName, setBetaName] = useState('');
  const [betaMessage, setBetaMessage] = useState('');
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaSuccess, setBetaSuccess] = useState(false);
  const [betaError, setBetaError] = useState('');

  // Feedback and UI alerts
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugLink, setDebugLink] = useState('');

  // Viewport responsiveness
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Lock to login view when public signup is disabled during private beta (unless invited)
  useEffect(() => {
    if (tenantConfig && !tenantConfig.allow_public_signup && !inviteToken && !isAgentApply) {
      setIsLogin(true);
    }
  }, [tenantConfig, inviteToken, isAgentApply]);

  // Validate Invitation Token (Team Member Invite or Agent Network Operator Invite)
  useEffect(() => {
    if (!inviteToken) return;
    const validateToken = async () => {
      setValidatingInvite(true);
      setInviteError('');
      try {
        // 1. Try organization team member invitation
        let res = await fetch(`${API_BASE}/api/v1/organizations/invitations/validate/${inviteToken}`, {
          headers: { 'X-Tenant-Domain': window.location.hostname }
        });
        let data;
        if (res.ok) {
          data = await res.json();
          data.is_team_member = true;
        } else {
          // 2. Fall back to agent network operator invitation
          res = await fetch(`${API_BASE}/api/v1/agent-network/invitations/validate/${inviteToken}`, {
            headers: { 'X-Tenant-Domain': window.location.hostname }
          });
          data = await res.json();
          if (!res.ok) {
            throw new Error(data.detail || 'This invitation link is invalid or has expired.');
          }
        }
        setInviteInfo(data);
        setIsLogin(false);
        setIsAgentApply(false);
        setRole(data.role || 'OPERATOR');
        if (data.recipient_email) setEmail(data.recipient_email);
        if (data.recipient_name) {
          const parts = data.recipient_name.trim().split(' ');
          setFirstName(parts[0] || '');
          setLastName(parts.slice(1).join(' ') || '');
        }
        if (data.organization_name) setOrgName(data.organization_name);
      } catch (err) {
        setInviteError(err.message);
      } finally {
        setValidatingInvite(false);
      }
    };
    validateToken();
  }, [inviteToken, API_BASE]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Debug autofill controls in local development
  const isLocalHost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.local') ||
    window.location.port === '3000'
  );
  const isDevelopmentEnv = !tenantConfig?.environment || tenantConfig?.environment === 'development';
  const showDebugAutofill = isLocalHost && isDevelopmentEnv;

  if (!tenantConfig) return null;

  // --- Handlers ---

  const handleStandardAuthSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');
    setDebugLink('');

    if (!isLogin && !tenantConfig.allow_public_signup) {
      setError('Public registration is currently disabled during private beta. Please contact your administrator.');
      setLoading(false);
      return;
    }

    const endpoint = isLogin ? '/api/v1/auth/login' : '/api/v1/auth/signup';
    const payload = isLogin 
      ? { email, password, remember: rememberMe }
      : { email, password, role, first_name: firstName, last_name: lastName };

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Authentication failed. Please try again.');
      }

      if (isLogin) {
        if (data.two_factor_required) {
          setTwoFactorChallenge({ token: data.two_factor_token, email_hint: data.email_hint });
          setMessage(data.message || `A 6-digit verification code has been sent to ${data.email_hint}`);
          setResendCooldown(60);
          return;
        }

        if (rememberMe) {
          localStorage.setItem('token', data.access_token);
          sessionStorage.removeItem('token');
        } else {
          sessionStorage.setItem('token', data.access_token);
          localStorage.removeItem('token');
        }
        setUser(data.user);
        setView('landing');
      } else {
        setMessage(data.message);
        if (data.debug_verification_link) {
          setDebugLink(data.debug_verification_link);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorVerify = async (e) => {
    e.preventDefault();
    if (!twoFactorChallenge || !twoFactorPin || twoFactorPin.length < 6) return;
    setTwoFactorLoading(true);
    setError('');
    setMessage('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          two_factor_token: twoFactorChallenge.token,
          pin: twoFactorPin.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to verify two-factor authentication PIN.');
      }

      if (rememberMe) {
        localStorage.setItem('token', data.access_token);
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', data.access_token);
        localStorage.removeItem('token');
      }
      setUser(data.user);
      setView('landing');
    } catch (err) {
      setError(err.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleTwoFactorResend = async () => {
    if (!twoFactorChallenge || resendCooldown > 0) return;
    setTwoFactorLoading(true);
    setError('');
    setMessage('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/2fa/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          two_factor_token: twoFactorChallenge.token
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to resend PIN.');
      }

      setMessage(data.message || 'A new verification code has been sent.');
      setResendCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleTeamInviteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          invite_token: inviteToken,
          password,
          first_name: firstName,
          last_name: lastName
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to join organization.');
      }

      if (data.access_token) {
        sessionStorage.setItem('token', data.access_token);
        localStorage.removeItem('token');
        setUser(data.user);
        setView('landing');
      } else {
        setMessage('Registration complete! Please log in.');
        setIsLogin(true);
        setInviteInfo(null);
        setInviteToken('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOperatorInviteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    if (!orgName.trim()) {
      setError('Organisation name is required to complete registration.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/register-operator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          invite_token: inviteToken,
          password,
          first_name: firstName,
          last_name: lastName,
          phone_number: phone,
          organization_name: orgName.trim(),
          website_address: orgWebsite.trim() || undefined,
          enquiries_email: orgEnquiriesEmail.trim() || email,
          billing_address: billingAddress.trim() || undefined,
          billing_city: billingCity.trim() || undefined,
          billing_state: billingState.trim() || undefined,
          billing_zip: billingZip.trim() || undefined,
          billing_country: billingCountry.trim() || undefined
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Operator registration failed.');
      }

      // If token is returned, authenticate directly
      if (data.access_token) {
        sessionStorage.setItem('token', data.access_token);
        localStorage.removeItem('token');
        setUser(data.user);
        setView('landing');
      } else {
        setMessage('Registration complete! Please log in with your credentials.');
        setIsLogin(true);
        setInviteInfo(null);
        setInviteToken('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentApplySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/apply-agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          email: agentForm.email,
          password: agentForm.password,
          first_name: agentForm.first_name,
          last_name: agentForm.last_name,
          phone_number: agentForm.phone_number,
          company_name: agentForm.company_name,
          billing_country: agentForm.billing_country,
          payout_details: {
            method: agentForm.payout_method,
            paypal_email: agentForm.paypal_email,
            bank_name: agentForm.bank_name,
            account_number: agentForm.account_number,
            routing_swift: agentForm.routing_swift,
            experience: agentForm.experience
          }
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to submit agent application.');
      }

      setAgentApplySuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider) => {
    setLoading(true);
    setMessage('');
    setError('');
    setDebugLink('');

    try {
      const mockToken = `mock_${provider}_id_token_${Math.random().toString(36).substring(7)}`;
      const res = await fetch(`${API_BASE}/api/v1/auth/${provider}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ id_token: mockToken, remember: rememberMe })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || `${provider} authentication failed.`);
      }

      if (rememberMe) {
        localStorage.setItem('token', data.access_token);
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', data.access_token);
        localStorage.removeItem('token');
      }
      setUser(data.user);
      setView('landing');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tenantDomain = (tenantConfig?.domain_name && tenantConfig.domain_name !== 'localhost')
    ? tenantConfig.domain_name
    : (typeof window !== 'undefined' && window.location.hostname && window.location.hostname !== 'localhost'
        ? window.location.hostname
        : 'trophycoin.io');

  const handleBetaRequestSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!betaEmail || !betaEmail.includes('@')) {
      setBetaError('Please enter a valid email address.');
      return;
    }
    setBetaLoading(true);
    setBetaError('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/request-beta-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': tenantDomain
        },
        body: JSON.stringify({
          email: betaEmail.trim().toLowerCase(),
          name: betaName.trim(),
          message: betaMessage.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to submit beta access request');
      }

      setBetaSuccess(true);
      setMessage('Beta request received! Your access application has been submitted to the platform administrators.');
    } catch (err) {
      setBetaError(err.message || 'Error submitting beta access request. Please try again.');
    } finally {
      setBetaLoading(false);
    }
  };

  const formWidth = isMobile ? '95%' : (inviteInfo || isAgentApply) ? '540px' : '450px';
  const paddingSize = isMobile ? '24px' : '36px';

  return (
    <main style={{
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px 20px',
      background: tenantConfig.background_url
        ? `url(${tenantConfig.background_url}) no-repeat center center / cover`
        : 'var(--bg-color)',
      minHeight: '75vh'
    }}>
      <div style={{
        width: formWidth,
        maxWidth: '100%',
        background: 'var(--panel-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: paddingSize,
        boxShadow: 'var(--card-shadow)',
        backdropFilter: 'blur(8px)',
        boxSizing: 'border-box'
      }}>
        {/* Logo and Brand Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          {tenantConfig.logo_url ? (
            <img 
              src={tenantConfig.logo_url} 
              alt={tenantConfig.brand_name} 
              style={{ maxHeight: '45px', marginBottom: '10px', objectFit: 'contain' }}
            />
          ) : (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)', marginBottom: '10px' }}>
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
              <path d="M4 22h16" />
              <path d="M10 14.66V17c0 .55-.45 1-1 1H4v2h16v-2h-5c-.55 0-1-.45-1-1v-2.34" />
              <path d="M12 2a6 6 0 0 1 6 6v5a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8a6 6 0 0 1 6-6z" />
            </svg>
          )}

          <h2 style={{ 
            color: 'var(--primary-color)', 
            fontSize: isMobile ? '22px' : '25px', 
            fontWeight: '700',
            margin: '5px 0'
          }}>
            {twoFactorChallenge
              ? 'Two-Factor Authentication'
              : isAgentApply 
              ? 'Apply as an Agent Partner'
              : inviteInfo
              ? (inviteInfo.is_team_member ? 'Join Team' : 'Operator Organization Onboarding')
              : isLogin 
              ? `Sign In to ${tenantConfig.brand_name}` 
              : `Create Your Account`}
          </h2>

          <p style={{ color: 'var(--text-mute)', fontSize: '13px', margin: 0 }}>
            {twoFactorChallenge
              ? `Enter the 6-digit verification code sent to ${twoFactorChallenge.maskedEmail}`
              : isAgentApply
              ? 'Join our global partner network to source operators and earn monthly commissions.'
              : inviteInfo
              ? (inviteInfo.is_team_member ? `Invited by ${inviteInfo.inviter_name || 'an administrator'} to join ${inviteInfo.organization_name}` : `Authorized invitation from ${inviteInfo.agent_name || 'Agent Partner'}`)
              : isLogin 
              ? 'Enter your credentials to access the campaign portal' 
              : 'Create an account to access custom digital coin collections'}
          </p>
        </div>

        {/* Validating Invitation Spinner */}
        {validatingInvite && (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-mute)', fontSize: '13px' }}>
            Validating partner invitation token...
          </div>
        )}

        {/* Invalid / Expired Invitation Alert */}
        {inviteError && (
          <div style={{
            backgroundColor: 'rgba(248, 81, 73, 0.15)',
            border: '1px solid rgba(248, 81, 73, 0.4)',
            borderRadius: '6px',
            color: '#ff7b72',
            padding: '14px',
            fontSize: '13px',
            marginBottom: '20px',
            lineHeight: '1.4'
          }}>
            <strong>Invitation Notice:</strong> {inviteError}
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  setInviteToken('');
                  setInviteError('');
                  setIsLogin(true);
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--primary-color)',
                  border: '1px solid var(--primary-color)',
                  borderRadius: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Proceed to Standard Sign In
              </button>
            </div>
          </div>
        )}

        {/* Branded Operator Invitation Header Banner */}
        {inviteInfo && !validatingInvite && (
          <div style={{
            backgroundColor: 'rgba(99, 102, 241, 0.12)',
            border: '1px solid rgba(99, 102, 241, 0.35)',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '20px',
            color: 'var(--text-main)',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)', flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <div>
              <span style={{ fontWeight: '700', color: 'var(--primary-color)' }}>
                Welcome to {tenantConfig.brand_name}!
              </span>
              <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
                {inviteInfo.is_team_member ? (
                  <>You have been invited to join <strong>{inviteInfo.organization_name}</strong> as an authorized operator.</>
                ) : (
                  <>Your account will be enrolled with <strong>OPERATOR</strong> clearance and connected to agent partner <strong>{inviteInfo.agent_name}</strong>.</>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Private Beta Notice Banner (Only for uninvited standard signup) */}
        {!tenantConfig.allow_public_signup && !inviteInfo && !isAgentApply && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: 'rgba(234, 179, 8, 0.12)',
            border: '1px solid rgba(234, 179, 8, 0.35)',
            borderRadius: '6px',
            padding: '10px 14px',
            marginBottom: '20px',
            color: '#eab308',
            fontSize: '12px',
            lineHeight: '1.4'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <div>
              <span style={{ fontWeight: '600' }}>Private Beta Access Only:</span> Public self-registration is closed. Accounts are provisioned and approved by your organization administrator.
            </div>
          </div>
        )}

        {/* Action Alerts */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(248, 81, 73, 0.15)',
            border: '1px solid rgba(248, 81, 73, 0.4)',
            borderRadius: '6px',
            color: '#ff7b72',
            padding: '12px',
            fontSize: '13px',
            marginBottom: '20px',
            lineHeight: '1.4'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {message && (
          <div style={{
            backgroundColor: 'rgba(56, 139, 253, 0.15)',
            border: '1px solid rgba(56, 139, 253, 0.4)',
            borderRadius: '6px',
            color: '#58a6ff',
            padding: '12px',
            fontSize: '13px',
            marginBottom: '20px',
            lineHeight: '1.4'
          }}>
            {message}
          </div>
        )}

        {debugLink && (
          <div style={{
            backgroundColor: 'rgba(46, 160, 67, 0.15)',
            border: '1px solid rgba(46, 160, 67, 0.4)',
            borderRadius: '6px',
            color: '#3fb950',
            padding: '12px',
            fontSize: '13px',
            marginBottom: '20px',
            lineHeight: '1.4'
          }}>
            <strong>[Mock Email verification]</strong><br />
            Since emails are mocked locally, click this link to verify this email:<br />
            <a 
              href={debugLink}
              target="_blank"
              rel="noopener noreferrer" 
              style={{ color: '#58a6ff', textDecoration: 'underline', wordBreak: 'break-all', display: 'inline-block', marginTop: '8px' }}
            >
              Verify Email Transaction link
            </a>
          </div>
        )}

        {/* ============================================================== */}
        {/* VIEW 1: AGENT APPLICATION FLOW                                 */}
        {/* ============================================================== */}
        {isAgentApply ? (
          agentApplySuccess ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '42px' }}>🎉</div>
              <h3 style={{ margin: 0, fontSize: '20px', color: 'var(--primary-color)' }}>
                Application Received!
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.6' }}>
                Thank you for applying to become an independent agent partner for {tenantConfig.brand_name}. Your profile and payout configuration are currently pending tenant administrator review.
              </p>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '12px',
                color: 'var(--text-mute)',
                textAlign: 'left'
              }}>
                <strong style={{ color: 'var(--text-main)', display: 'block', marginBottom: '4px' }}>Next Steps:</strong>
                1. Once approved, you will receive an activation email with your assigned contract terms.<br />
                2. You will be able to log in to access your Agent Portal, generate operator invite links, and claim your monthly sample coupons.
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsAgentApply(false);
                  setIsLogin(true);
                  setAgentApplySuccess(false);
                }}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'var(--button-primary-text, #ffffff)',
                  border: 'none',
                  borderRadius: 'var(--radius-md, 6px)',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleAgentApplySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>First Name *</label>
                  <input
                    type="text"
                    required
                    value={agentForm.first_name}
                    onChange={(e) => setAgentForm({ ...agentForm, first_name: e.target.value })}
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Last Name *</label>
                  <input
                    type="text"
                    required
                    value={agentForm.last_name}
                    onChange={(e) => setAgentForm({ ...agentForm, last_name: e.target.value })}
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Email Address *</label>
                <input
                  type="email"
                  required
                  value={agentForm.email}
                  onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                  placeholder="agent@example.com"
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Password *</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={agentForm.password}
                    onChange={(e) => setAgentForm({ ...agentForm, password: e.target.value })}
                    style={{
                      flex: 1, backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 36px 8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute', right: '8px', background: 'none', border: 'none',
                      cursor: 'pointer', color: 'var(--text-mute)', padding: '2px'
                    }}
                  >
                    {showPassword ? '👁️' : '🔒'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Phone Number</label>
                  <input
                    type="tel"
                    value={agentForm.phone_number}
                    onChange={(e) => setAgentForm({ ...agentForm, phone_number: e.target.value })}
                    placeholder="+1 555-0192"
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Country / Region</label>
                  <input
                    type="text"
                    value={agentForm.billing_country}
                    onChange={(e) => setAgentForm({ ...agentForm, billing_country: e.target.value })}
                    placeholder="e.g. United Kingdom"
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Company / Agency Name</label>
                <input
                  type="text"
                  value={agentForm.company_name}
                  onChange={(e) => setAgentForm({ ...agentForm, company_name: e.target.value })}
                  placeholder="Apex Promotions Ltd."
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>
                  Experience & Commercial Network Summary
                </label>
                <textarea
                  rows={2}
                  value={agentForm.experience}
                  onChange={(e) => setAgentForm({ ...agentForm, experience: e.target.value })}
                  placeholder="Describe your current client base, target industries, or regional outreach..."
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>

              {/* Payout Details Section */}
              <div style={{
                borderTop: '1px solid var(--border-color)',
                paddingTop: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color)' }}>
                  Commission Payout Preferences
                </span>

                <div style={{ display: 'flex', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="payout_method"
                      value="paypal"
                      checked={agentForm.payout_method === 'paypal'}
                      onChange={() => setAgentForm({ ...agentForm, payout_method: 'paypal' })}
                    />
                    PayPal
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="payout_method"
                      value="bank_wire"
                      checked={agentForm.payout_method === 'bank_wire'}
                      onChange={() => setAgentForm({ ...agentForm, payout_method: 'bank_wire' })}
                    />
                    Direct Bank Transfer / Wire
                  </label>
                </div>

                {agentForm.payout_method === 'paypal' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>PayPal Account Email</label>
                    <input
                      type="email"
                      value={agentForm.paypal_email}
                      onChange={(e) => setAgentForm({ ...agentForm, paypal_email: e.target.value })}
                      placeholder="paypal@example.com"
                      style={{
                        backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                        borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Bank Name</label>
                      <input
                        type="text"
                        value={agentForm.bank_name}
                        onChange={(e) => setAgentForm({ ...agentForm, bank_name: e.target.value })}
                        placeholder="Barclays / Chase"
                        style={{
                          backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                          borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Account / IBAN</label>
                      <input
                        type="text"
                        value={agentForm.account_number}
                        onChange={(e) => setAgentForm({ ...agentForm, account_number: e.target.value })}
                        placeholder="GB00..."
                        style={{
                          backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                          borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'var(--button-primary-text, #ffffff)',
                  border: 'none',
                  borderRadius: 'var(--radius-md, 6px)',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                {loading ? 'Submitting Application...' : 'Submit Agent Application'}
              </button>

              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsAgentApply(false);
                    setIsLogin(true);
                  }}
                  style={{
                    background: 'none', border: 'none', color: '#58a6ff',
                    cursor: 'pointer', fontSize: '13px'
                  }}
                >
                  &larr; Back to Member Sign In
                </button>
              </div>
            </form>
          )
        ) : inviteInfo ? (
          /* ============================================================== */
          /* VIEW 2: OPERATOR INVITATION ONBOARDING                         */
          /* ============================================================== */
          inviteInfo.is_team_member ? (
            <form onSubmit={handleTeamInviteSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{
                padding: '16px',
                backgroundColor: 'rgba(56, 139, 253, 0.12)',
                border: '1px solid rgba(56, 139, 253, 0.3)',
                borderRadius: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--primary-color)', marginBottom: '4px' }}>
                  Team Member Invitation
                </div>
                <div style={{ fontSize: '15px', color: 'var(--text-main)', fontWeight: '700' }}>
                  Join {inviteInfo.organization_name || 'Organization'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '4px', lineHeight: '1.4' }}>
                  You have been invited to join the team as an authorized operator. You will share collaborative access to organization orders, artwork, cards, and vaulted coins.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>First Name *</label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Last Name *</label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Email Address *</label>
                <input
                  type="email"
                  required
                  value={email}
                  disabled
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--text-mute)', fontSize: '13px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Create Password *</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      flex: 1, backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 36px 8px 10px', color: 'var(--input-text)', fontSize: '13px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute', right: '8px', background: 'none', border: 'none',
                      cursor: 'pointer', color: 'var(--text-mute)', padding: '2px'
                    }}
                  >
                    {showPassword ? '👁️' : '🔒'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'var(--button-primary-text, #ffffff)',
                  border: 'none',
                  borderRadius: 'var(--radius-md, 6px)',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                {loading ? 'Joining Team...' : 'Accept Invitation & Access Portal'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOperatorInviteSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color)' }}>
                Step 1 &bull; Personal Credentials
              </div>

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>First Name *</label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Last Name *</label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Email Address *</label>
                <input
                  type="email"
                  required
                  value={email}
                  disabled
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--text-mute)', fontSize: '13px'
                  }}
                />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555-0100"
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Create Password *</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    flex: 1, backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 36px 8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: '8px', background: 'none', border: 'none',
                    cursor: 'pointer', color: 'var(--text-mute)', padding: '2px'
                  }}
                >
                  {showPassword ? '👁️' : '🔒'}
                </button>
              </div>
            </div>

            {/* Organization Mandatory Details */}
            <div style={{
              borderTop: '1px solid var(--border-color)',
              paddingTop: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color)' }}>
                Step 2 &bull; Mandatory Organization Profile
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>Organization / Business Name *</label>
                <input
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Serengeti Safari Lodge"
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Website Address</label>
                  <input
                    type="url"
                    value={orgWebsite}
                    onChange={(e) => setOrgWebsite(e.target.value)}
                    placeholder="https://example.com"
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Enquiries Email</label>
                  <input
                    type="email"
                    value={orgEnquiriesEmail}
                    onChange={(e) => setOrgEnquiriesEmail(e.target.value)}
                    placeholder={email || "info@example.com"}
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Corporate Street Address</label>
                <input
                  type="text"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  placeholder="100 Commercial Way"
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>City</label>
                  <input
                    type="text"
                    value={billingCity}
                    onChange={(e) => setBillingCity(e.target.value)}
                    placeholder="Arusha"
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>State / Region</label>
                  <input
                    type="text"
                    value={billingState}
                    onChange={(e) => setBillingState(e.target.value)}
                    placeholder="Northern"
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Postal Code</label>
                  <input
                    type="text"
                    value={billingZip}
                    onChange={(e) => setBillingZip(e.target.value)}
                    placeholder="23100"
                    style={{
                      backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                      borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '300px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Country</label>
                <input
                  type="text"
                  value={billingCountry}
                  onChange={(e) => setBillingCountry(e.target.value)}
                  placeholder="Tanzania"
                  style={{
                    backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px'
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'var(--button-primary-text, #ffffff)',
                border: 'none',
                borderRadius: 'var(--radius-md, 6px)',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              {loading ? 'Activating Operator Account...' : 'Complete Registration & Access Portal'}
            </button>
          </form>
          )
        ) : twoFactorChallenge ? (
          /* ============================================================== */
          /* VIEW 3A: TWO-FACTOR AUTHENTICATION PIN VERIFICATION            */
          /* ============================================================== */
          <form onSubmit={handleTwoFactorVerify} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 0 6px 0'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: 'rgba(99, 102, 241, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-mute)', textAlign: 'center' }}>
                Security check: Please enter the 6-digit one-time code sent to <strong>{twoFactorChallenge.maskedEmail}</strong>.
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: 'var(--text-main)', textAlign: 'center', fontWeight: '600' }}>
                6-Digit Security Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                autoComplete="one-time-code"
                value={twoFactorPin}
                onChange={(e) => setTwoFactorPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="------"
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '2px solid var(--primary-color)',
                  borderRadius: '8px',
                  padding: '14px',
                  color: 'var(--input-text)',
                  fontSize: '24px',
                  fontWeight: '700',
                  letterSpacing: '12px',
                  textAlign: 'center',
                  outline: 'none'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={twoFactorLoading || twoFactorPin.length < 6}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'var(--button-primary-text, #ffffff)',
                border: 'none',
                borderRadius: 'var(--radius-md, 6px)',
                padding: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: twoFactorPin.length < 6 || twoFactorLoading ? 'not-allowed' : 'pointer',
                opacity: twoFactorLoading || twoFactorPin.length < 6 ? '0.6' : '1',
                transition: 'opacity 0.2s'
              }}
            >
              {twoFactorLoading ? 'Verifying PIN...' : 'Verify and Sign In'}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingTop: '12px', borderTop: '1px solid #30363d' }}>
              <button
                type="button"
                onClick={() => {
                  setTwoFactorChallenge(null);
                  setTwoFactorPin('');
                  setError('');
                  setMessage('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-mute)',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                &larr; Back to Sign In
              </button>

              <button
                type="button"
                onClick={handleTwoFactorResend}
                disabled={twoFactorLoading || resendCooldown > 0}
                style={{
                  background: 'none',
                  border: 'none',
                  color: resendCooldown > 0 ? 'var(--text-mute)' : '#58a6ff',
                  cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                  fontSize: '13px'
                }}
              >
                {resendCooldown > 0 ? `Resend PIN (${resendCooldown}s)` : 'Resend Code'}
              </button>
            </div>
          </form>
        ) : (
          /* ============================================================== */
          /* VIEW 3B: STANDARD LOGIN / SIGNUP                               */
          /* ============================================================== */
          <>
            <form onSubmit={handleStandardAuthSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {!isLogin && (
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '15px'
                }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '13px', color: 'var(--text-main)' }}>First Name</label>
                    <input 
                      type="text" 
                      value={firstName} 
                      onChange={(e) => setFirstName(e.target.value)} 
                      required
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '13px', color: 'var(--text-main)' }}>Last Name</label>
                    <input 
                      type="text" 
                      value={lastName} 
                      onChange={(e) => setLastName(e.target.value)} 
                      required
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-main)' }}>Email Address</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: 'var(--input-text)',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-main)' }}>Password</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    required
                    minLength={8}
                    style={{
                      flex: 1,
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px 40px 10px 10px',
                      color: 'var(--input-text)',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px',
                      color: 'var(--text-mute)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    )}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '13px', color: 'var(--text-main)' }}>Select Account Scope</label>
                  <select 
                    value={role} 
                    onChange={(e) => setRole(e.target.value)} 
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '14px'
                    }}
                  >
                    <option value="END_USER">End User Profile</option>
                    <option value="OPERATOR">Operator Profile</option>
                  </select>
                </div>
              )}

              {isLogin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: 'var(--primary-color)'
                    }}
                  />
                  <label 
                    htmlFor="rememberMe" 
                    style={{ 
                      fontSize: '13px', 
                      color: 'var(--text-mute)', 
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    Keep me signed in
                  </label>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'var(--button-primary-text, #ffffff)',
                  border: 'none',
                  borderRadius: 'var(--radius-md, 6px)',
                  padding: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '15px',
                  transition: 'opacity 0.2s',
                  opacity: loading ? '0.7' : '1'
                }}
              >
                {loading ? 'Processing Transaction...' : isLogin ? 'Sign In' : 'Register Account'}
              </button>
            </form>

            {/* OR Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              margin: '20px 0',
              color: '#8b949e',
              fontSize: '12px'
            }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#30363d' }}></div>
              <span style={{ padding: '0 10px' }}>or continue with</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#30363d' }}></div>
            </div>

            {/* OAuth Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px',
              width: '100%',
              boxSizing: 'border-box',
              marginBottom: '20px'
            }}>
              {/* Google Login */}
              <button
                onClick={() => handleOAuthLogin('google')}
                disabled={loading}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  backgroundColor: '#ffffff',
                  color: '#1f2328',
                  border: '1px solid #d0d7de',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  opacity: loading ? '0.7' : '1',
                  boxSizing: 'border-box'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f6f8fa'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.47h4.84c-.21 1.12-.84 2.07-1.79 2.7v2.24h2.9c1.7-1.57 2.69-3.88 2.69-6.57zm-8.64 8.8c2.43 0 4.47-.8 5.96-2.18l-2.9-2.24c-.8.54-1.84.87-3.06.87-2.35 0-4.34-1.58-5.05-3.71H.95v2.32C2.43 15.11 5.48 18 9 18z"/>
                  <path fill="#34A853" d="M3.95 10.74c-.18-.54-.28-1.12-.28-1.74s.1-1.2.28-1.74V4.94H.95C.34 6.16 0 7.54 0 9s.34 2.84.95 4.06l3-2.32z"/>
                  <path fill="#FBBC05" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0 5.48 0 2.43 2.89.95 4.94l3 2.32C4.66 5.16 6.65 3.58 9 3.58z"/>
                </svg>
                Google
              </button>

              {/* Apple Login */}
              <button
                onClick={() => handleOAuthLogin('apple')}
                disabled={loading}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  border: '1px solid #30363d',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  opacity: loading ? '0.7' : '1',
                  boxSizing: 'border-box'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1f242b'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#000000'}
              >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="currentColor">
                  <path d="M15.56 10.02c-.04-2.18 1.78-3.23 1.86-3.28-1.01-1.48-2.59-1.68-3.15-1.72-1.34-.14-2.63.79-3.31.79-.68 0-1.74-.77-2.87-.75-1.48.02-2.85.86-3.61 2.19-1.54 2.68-.39 6.64 1.1 8.79.73 1.05 1.59 2.23 2.73 2.19 1.1-.04 1.51-.71 2.83-.71s1.7.71 2.85.69c1.17-.02 1.94-1.07 2.67-2.12.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.34-3.52zM12.98 3.23c.6-.73.99-1.75.88-2.76-.87.03-1.92.58-2.55 1.32-.56.64-1.05 1.68-.92 2.68.97.08 1.98-.51 2.59-1.24z"/>
                </svg>
                Apple
              </button>
            </div>

            {/* Toggle between Signup and Login */}
            {tenantConfig.allow_public_signup ? (
              <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid #30363d', paddingTop: '15px' }}>
                <button 
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setMessage('');
                    setDebugLink('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#58a6ff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textDecoration: 'none'
                  }}
                >
                  {isLogin ? "Don't have an account? Create one here" : "Already have an account? Sign In here"}
                </button>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                marginTop: '16px',
                borderTop: '1px solid #30363d',
                paddingTop: '15px',
                color: '#8b949e',
                fontSize: '12px',
                lineHeight: '1.5'
              }}>
                The site is currently accepting beta users only. If you want to be considered for the beta program, please{' '}
                <button
                  type="button"
                  data-testid="request-beta-access-link"
                  onClick={() => {
                    setShowBetaModal(true);
                    setBetaSuccess(false);
                    setBetaError('');
                    if (!betaEmail && email) setBetaEmail(email);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-color, #58a6ff)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textDecoration: 'underline',
                    padding: 0,
                    fontFamily: 'inherit',
                    fontWeight: '600'
                  }}
                >
                  click here to fill in the request form
                </button>
                {' '}to apply.
              </div>
            )}
          </>
        )}

        {/* Global Agent Partner Application Entry Point */}
        {!isAgentApply && !inviteInfo && !twoFactorChallenge && (
          <div style={{ textAlign: 'center', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #30363d' }}>
            <button
              type="button"
              onClick={() => {
                setIsAgentApply(true);
                setError('');
                setMessage('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary-color, #58a6ff)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>💼</span>
              <span>Interested in earning commissions? Apply as an Agent &rarr;</span>
            </button>
          </div>
        )}

        {/* Onboarding & Tutorial Video Link */}
        {tenantConfig?.onboarding_video_url && (
          <div style={{ textAlign: 'center', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #30363d' }}>
            <a
              href={tenantConfig?.id ? `/tutorial?tenant_id=${tenantConfig.id}&from=login` : '/tutorial?from=login'}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="auth-tutorial-video-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--primary-color, #58a6ff)',
                fontSize: '13px',
                fontWeight: '500',
                textDecoration: 'none',
                transition: 'opacity 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              <span>{tenantConfig?.onboarding_video_title ? `Watch Tutorial: ${tenantConfig.onboarding_video_title}` : 'Watch Tutorial: How to log coin data'}</span>
            </a>
          </div>
        )}

        {/* Back to Home Link */}
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button 
            type="button"
            onClick={() => setView('landing')}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            &larr; Back to Landing Page
          </button>
        </div>

        {/* Localhost Debug Autofill Helper */}
        {showDebugAutofill && isLogin && !isAgentApply && !inviteInfo && (
          <div style={{
            marginTop: '22px',
            borderTop: '1px solid #30363d',
            paddingTop: '15px',
            textAlign: 'left'
          }}>
            <div style={{ 
              fontSize: '11px', 
              fontWeight: 'bold', 
              color: '#8b949e', 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              marginBottom: '8px'
            }}>
              Debug Auto-Fill Users
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                { label: 'Global Admin', email: 'admin@trophycoin.io', pass: 'admin_pass_123' },
                { label: 'Fisherman Admin', email: 'admin@fishermanstale.com', pass: 'fisherman_admin_123' },
                { label: 'Fisherman Operator', email: 'operator@fishermanstale.com', pass: 'fisherman_operator_123' },
                { label: 'Fisherman EndUser', email: 'user@fishermanstale.com', pass: 'fisherman_user_123' },
                { label: 'Sandbox Operator', email: 'operator@localhost', pass: 'sandbox_operator_123' }
              ].map((u, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    setEmail(u.email);
                    setPassword(u.pass);
                  }}
                  style={{
                    backgroundColor: '#1b1b1d',
                    border: '1px solid #3c3c42',
                    borderRadius: '4px',
                    color: '#dfdfe3',
                    padding: '6px 10px',
                    fontSize: '11px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    transition: 'all 0.15s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#2d2d31';
                    e.currentTarget.style.borderColor = '#55555c';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#1b1b1d';
                    e.currentTarget.style.borderColor = '#3c3c42';
                  }}
                >
                  <span style={{ fontWeight: 'bold' }}>{u.label}</span>
                  <span style={{ color: '#8e8e93', fontSize: '10px' }}>{u.email}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Beta Access Request Modal Dialog */}
      {showBetaModal && (
        <div
          data-testid="beta-access-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
            boxSizing: 'border-box'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowBetaModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--modal-bg, var(--panel-bg, #161b22))',
              border: '1px solid var(--modal-border, var(--border-color, #30363d))',
              borderRadius: 'var(--radius-lg, 12px)',
              padding: '24px',
              maxWidth: '460px',
              width: '100%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
              color: 'var(--text-main, #f8fafc)',
              boxSizing: 'border-box',
              position: 'relative'
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              data-testid="close-beta-modal-btn"
              onClick={() => setShowBetaModal(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: 'var(--text-mute, #8b949e)',
                cursor: 'pointer',
                fontSize: '18px',
                lineHeight: '1',
                padding: '4px'
              }}
            >
              ✕
            </button>

            {betaSuccess ? (
              <div style={{ textAlign: 'center', padding: '16px 0 8px 0' }}>
                <div style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(46, 160, 67, 0.15)',
                  border: '1px solid rgba(46, 160, 67, 0.4)',
                  color: '#3fb950',
                  fontSize: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto'
                }}>
                  ✓
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: 'var(--text-main, #f8fafc)' }}>
                  Request Submitted!
                </h3>
                <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-mute, #8b949e)', lineHeight: '1.5' }}>
                  Thank you! Your email has been captured and submitted. The team will review your application and send you an invitation email once approved.
                </p>
                <button
                  type="button"
                  onClick={() => setShowBetaModal(false)}
                  style={{
                    backgroundColor: 'var(--primary-color, #58a6ff)',
                    color: 'var(--button-primary-text, #ffffff)',
                    border: 'none',
                    borderRadius: 'var(--radius-md, 6px)',
                    padding: '10px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleBetaRequestSubmit}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(234, 179, 8, 0.15)',
                    color: '#eab308',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}>
                    🚀
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '17px', color: 'var(--text-main, #f8fafc)', fontWeight: '600' }}>
                      Join the Beta Program
                    </h3>
                    <div style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)', marginTop: '2px' }}>
                      {tenantConfig.brand_name || 'TrophyCoin'} Private Beta
                    </div>
                  </div>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                  This site is currently accepting beta users only. Submit your email address below to apply for early access.
                </p>

                {betaError && (
                  <div style={{
                    backgroundColor: 'rgba(248, 81, 73, 0.15)',
                    border: '1px solid rgba(248, 81, 73, 0.4)',
                    borderRadius: '6px',
                    color: '#ff7b72',
                    padding: '10px 12px',
                    fontSize: '12px',
                    marginBottom: '14px',
                    lineHeight: '1.4'
                  }}>
                    {betaError}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-main, #f8fafc)', fontWeight: '500' }}>
                      Email Address <span style={{ color: '#f85149' }}>*</span>
                    </label>
                    <input
                      type="email"
                      required
                      autoFocus
                      data-testid="beta-email-input"
                      value={betaEmail}
                      onChange={(e) => setBetaEmail(e.target.value)}
                      placeholder="you@example.com"
                      style={{
                        backgroundColor: 'var(--input-bg, #0d1117)',
                        border: '1px solid var(--input-border, #30363d)',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        color: 'var(--input-text, #f8fafc)',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-main, #f8fafc)', fontWeight: '500' }}>
                      Your Name (Optional)
                    </label>
                    <input
                      type="text"
                      data-testid="beta-name-input"
                      value={betaName}
                      onChange={(e) => setBetaName(e.target.value)}
                      placeholder="First and last name"
                      style={{
                        backgroundColor: 'var(--input-bg, #0d1117)',
                        border: '1px solid var(--input-border, #30363d)',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        color: 'var(--input-text, #f8fafc)',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-main, #f8fafc)', fontWeight: '500' }}>
                      Use Case / Organization (Optional)
                    </label>
                    <textarea
                      rows={3}
                      data-testid="beta-message-input"
                      value={betaMessage}
                      onChange={(e) => setBetaMessage(e.target.value)}
                      placeholder="Tell us about how you plan to use custom coins or certificates..."
                      style={{
                        backgroundColor: 'var(--input-bg, #0d1117)',
                        border: '1px solid var(--input-border, #30363d)',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        color: 'var(--input-text, #f8fafc)',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowBetaModal(false)}
                    disabled={betaLoading}
                    style={{
                      backgroundColor: 'var(--button-secondary-bg, transparent)',
                      border: '1px solid var(--button-secondary-border, var(--border-color, #30363d))',
                      borderRadius: 'var(--radius-md, 6px)',
                      padding: '10px 16px',
                      color: 'var(--button-secondary-text, var(--text-mute, #8b949e))',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={betaLoading}
                    data-testid="submit-beta-request-btn"
                    style={{
                      backgroundColor: 'var(--primary-color, #58a6ff)',
                      border: 'none',
                      borderRadius: 'var(--radius-md, 6px)',
                      padding: '10px 18px',
                      color: 'var(--button-primary-text, #ffffff)',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: betaLoading ? 'not-allowed' : 'pointer',
                      opacity: betaLoading ? 0.7 : 1
                    }}
                  >
                    {betaLoading ? 'Submitting request...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
